<template>
  <AutoRefresh :callback="callback" :updatedAt="data.generatedAt" />
  <ContainerAlerts v-if="alerts.length" class="alerts" :alerts="alerts" />
  <template v-if="m">
    <div class="kpis">
      <StatisticsKpi label="État" :value="badge.label" :hint="`Réponse en ${c.responseMs} ms`" :tone="c.health === 'critical' ? 'bad' : c.health === 'warning' ? 'warn' : ''" />
      <StatisticsKpi label="En ligne depuis" :value="duration(m.uptimeSeconds)" :hint="`Démarré le ${dateTime(m.startedAt)}`" />
      <StatisticsKpi label="Mémoire" :value="bytes(memory.used)" :hint="memory.limit ? `${percent(memory.used / memory.limit * 100)} de la limite (${bytes(memory.limit)})` : 'Aucune limite sur ce container'" :tone="memoryTone === 'critical' ? 'bad' : memoryTone === 'warning' ? 'warn' : ''" />
      <StatisticsKpi label="CPU" :value="percent(cpu)" hint="Moyenne des 30 dernières secondes" />
      <StatisticsKpi v-if="hasDb" label="Base de données" :value="db.status === 'up' ? `${db.pingMs} ms` : 'Injoignable'" :hint="db.status === 'up' ? `Temps de réponse de ${db.name}` : (db.error ?? '')" :tone="db.status === 'up' ? '' : 'bad'" />
      <StatisticsKpi v-if="r" label="Requêtes" :value="integer(r.total)" :hint="`${integer(r.avgMs)} ms en moyenne, 95 % en moins de ${integer(r.p95Ms)} ms`" :tone="r.statuses['5xx'] ? 'warn' : ''" />
    </div>
    <ContainerExtras :extras="m.extras" />
  </template>
  <ContainerActivity v-if="c.activity && c.status === 'up'" :containerId="c.id" :refreshedAt="data.generatedAt" />
  <ContainerAudience v-if="c.audience" :app="c.audience" :refreshedAt="data.generatedAt" />
  <template v-if="m">
    <div class="charts">
      <StatisticsCard :span="6" title="Mémoire" subtitle="En Mo, un point toutes les 30 secondes depuis le démarrage du service">
        <StatisticsChart type="line" :labels="labels" :datasets="memoryDatasets" value-format="integer" />
      </StatisticsCard>
      <StatisticsCard :span="6" :title="hasEventLoop ? 'CPU et latence' : 'CPU'" :subtitle="hasEventLoop ? 'CPU en %, latence de la boucle d\'événements en ms (axe de droite)' : 'CPU en %'">
        <StatisticsChart type="line" :labels="labels" :datasets="cpuDatasets" value-format="percent" secondary-format="integer" />
      </StatisticsCard>
      <StatisticsCard v-if="r" :span="8" title="Requêtes par minute" subtitle="Soixante dernières minutes, hors supervision" :empty="!hasData(r.perMinute.map(row => row.count))">
        <StatisticsChart :labels="r.perMinute.map(row => minute(row.at))" :datasets="requestDatasets" value-format="integer" />
      </StatisticsCard>
      <StatisticsCard v-if="r" :span="4" title="Réponses HTTP" subtitle="Depuis le démarrage du service" :empty="!r.total">
        <StatisticsChart type="doughnut" :labels="STATUS_LABELS" :datasets="[{label: 'Réponses', data: STATUS_KEYS.map(key => r.statuses[key]), colors: STATUS_COLORS}]" value-format="integer" />
      </StatisticsCard>
      <StatisticsCard v-if="r" :span="12" title="Routes les plus appelées" subtitle="Depuis le démarrage du service">
        <p v-if="!r.routes.length" class="muted">Aucune requête depuis le démarrage.</p>
        <div v-else class="table-scroll">
          <table class="table">
            <thead>
              <tr>
                <th>Route</th>
                <th class="number">Appels</th>
                <th class="number">Erreurs serveur</th>
                <th class="number">Durée moyenne</th>
                <th class="number">Plus longue</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="route in r.routes" :key="route.route">
                <td><code>{{ route.route }}</code></td>
                <td class="number">{{ integer(route.count) }}</td>
                <td class="number" :class="{bad: route.errors}">{{ integer(route.errors) }}</td>
                <td class="number">{{ route.avgMs }} ms</td>
                <td class="number">{{ route.maxMs }} ms</td>
              </tr>
            </tbody>
          </table>
        </div>
      </StatisticsCard>
      <StatisticsCard :span="hasDb ? 6 : 12" title="Disques et dossiers" subtitle="Espace du système de fichiers du container">
        <div class="meters">
          <UsageBar v-for="disk in m.disks" :key="disk.filesystem + disk.mount" :label="`Disque ${disk.mount}`" :text="`${bytes(disk.usedBytes)} / ${bytes(disk.totalBytes)}`" :percent="disk.usedPercent" :tone="usageTone(disk.usedPercent, data.thresholds.diskPercent)" :hint="`${bytes(disk.availableBytes)} libres (${disk.filesystem})`" />
          <p v-if="!m.disks.length" class="muted">Mesure du disque indisponible.</p>
        </div>
        <dl v-if="m.folders.length" class="facts folders">
          <div v-for="folder in m.folders" :key="folder.name"><dt>Dossier {{ folder.name }}</dt><dd>{{ bytes(folder.sizeBytes) }}</dd></div>
        </dl>
      </StatisticsCard>
      <StatisticsCard v-if="hasDb" :span="6" title="Base de données" :subtitle="db.name ? `Base ${db.name}` : 'Base du service'">
        <p v-if="db.status !== 'up'" class="bad">Injoignable{{ db.error ? ` : ${db.error}` : '' }}</p>
        <dl v-else class="facts">
          <div><dt>Temps de réponse</dt><dd>{{ db.pingMs }} ms</dd></div>
          <div><dt>Collections</dt><dd>{{ integer(db.collections) }}</dd></div>
          <div><dt>Documents</dt><dd>{{ integer(db.objects) }}</dd></div>
          <div><dt>Données</dt><dd>{{ bytes(db.dataBytes) }}</dd></div>
          <div><dt>Stockage</dt><dd>{{ bytes(db.storageBytes) }}</dd></div>
          <div><dt>Index</dt><dd>{{ bytes(db.indexBytes) }}</dd></div>
        </dl>
      </StatisticsCard>
      <StatisticsCard :span="12" title="Informations">
        <dl class="facts info">
          <div><dt>Container</dt><dd>{{ c.container }}</dd></div>
          <div><dt>Identifiant du container</dt><dd>{{ m.hostname }}</dd></div>
          <div><dt>Accès</dt><dd>{{ c.access }}</dd></div>
          <div><dt>Version du service</dt><dd>{{ m.version ?? '—' }}</dd></div>
          <div><dt>{{ runtime.name }}</dt><dd>{{ runtime.version }}</dd></div>
          <div><dt>Environnement</dt><dd>{{ m.environment ?? '—' }}</dd></div>
          <div><dt>Processus</dt><dd>{{ m.pid }}</dd></div>
          <div v-if="m.process.heapUsedBytes !== null"><dt>Mémoire JavaScript</dt><dd>{{ bytes(m.process.heapUsedBytes) }} / {{ bytes(m.process.heapLimitBytes) }}</dd></div>
          <div><dt>Mémoire du processus</dt><dd>{{ bytes(m.process.rssBytes) }}</dd></div>
          <div><dt>Source de la mémoire</dt><dd>{{ m.container.memory ? m.container.memory.source : 'Processus (limites du container inconnues)' }}</dd></div>
        </dl>
      </StatisticsCard>
    </div>
  </template>
  <div v-else class="charts">
    <StatisticsCard :span="12" title="Aucune mesure">
      <p class="muted">{{ c.status === 'unsupported' ? "Ce container répond mais ne propose pas la supervision : voyez l'alerte ci-dessus, puis redéployez-le avec la dernière version du service." : "Le container ne répond pas : vérifiez qu'il est démarré, puis actualisez la page." }}</p>
    </StatisticsCard>
  </div>
  <div v-if="c.status === 'up' && c.logs !== false" class="charts">
    <StatisticsCard :span="12" title="Logs" subtitle="Les dernières lignes écrites par le service (jetons et mots de passe masqués)">
      <ContainerLogs :containerId="c.id" />
    </StatisticsCard>
  </div>
</template>

<script>
import StatisticsKpi from "@/statistics/components/StatisticsKpi.vue";
import StatisticsCard from "@/statistics/components/StatisticsCard.vue";
import StatisticsChart from "@/statistics/components/StatisticsChart.vue";
import {hasData, integer, percent} from "@/statistics/utils/format";
import {PALETTE} from "@/statistics/utils/chartTheme";
import AutoRefresh from "@/administration/components/AutoRefresh.vue";
import ContainerAlerts from "@/administration/components/ContainerAlerts.vue";
import ContainerLogs from "@/administration/components/ContainerLogs.vue";
import ContainerExtras from "@/administration/components/ContainerExtras.vue";
import ContainerActivity from "@/administration/components/ContainerActivity.vue";
import ContainerAudience from "@/administration/components/ContainerAudience.vue";
import UsageBar from "@/administration/components/UsageBar.vue";
import {bytes, containerBadge, dateTime, duration, megabytes, minute, usageTone} from "@/administration/utils";

const STATUS_KEYS = ['2xx', '3xx', '4xx', '5xx', 'other'];
const STATUS_LABELS = ['Succès (2xx)', 'Redirections (3xx)', 'Erreurs client (4xx)', 'Erreurs serveur (5xx)', 'Autres'];
const STATUS_COLORS = ['#3fbd63', '#4a67ad', '#ef9400', '#ea4e2c', '#c2cbdc'];

/**
 * Un container : état, mémoire, CPU, disques, base de données, requêtes, logs (sauf les fronts, qui n'en gardent pas).
 * callback : recharge les mesures.
 */
export default {
  name: 'ContainerDetail',
  components: {StatisticsKpi, StatisticsCard, StatisticsChart, AutoRefresh, ContainerAlerts, ContainerLogs, ContainerExtras, ContainerActivity, ContainerAudience, UsageBar},
  props: {
    data: {type: Object, required: true},
    callback: {type: Function, required: true},
  },
  data() {
    return {STATUS_KEYS, STATUS_LABELS, STATUS_COLORS};
  },
  computed: {
    c() {
      return this.data.container;
    },
    // Mesures du service, absentes quand il ne répond pas
    m() {
      return this.c.metrics;
    },
    // Requêtes HTTP du service : absentes pour un front (servi par un serveur de fichiers)
    r() {
      return this.m.requests;
    },
    db() {
      return this.m.database;
    },
    // Les services sans base (générateur de PDF, fronts) l'annoncent par le statut « none »
    hasDb() {
      return this.db.status !== 'none';
    },
    hasEventLoop() {
      return this.m.history.some(sample => sample.eventLoopP99Ms !== null);
    },
    // Langage et version : Node.js, Python ou Node.js + serve (front)
    runtime() {
      return this.m.runtime ?? {name: 'Node.js', version: this.m.node};
    },
    badge() {
      return containerBadge(this.c);
    },
    // Alertes du container, puis celles de la machine hôte
    alerts() {
      return [...this.c.alerts, ...this.data.alerts];
    },
    // Mémoire du container (comme docker la compte), à défaut celle du processus
    memory() {
      const memory = this.m.container.memory;
      return memory ? {used: memory.usedBytes, limit: memory.limitBytes} : {used: this.m.process.rssBytes, limit: null};
    },
    memoryTone() {
      return this.memory.limit ? usageTone(this.memory.used / this.memory.limit * 100, this.data.thresholds.containerMemoryPercent) : 'ok';
    },
    cpu() {
      return this.m.container.cpuPercent ?? this.m.process.cpuPercent;
    },
    labels() {
      return this.m.history.map(sample => minute(sample.at));
    },
    memoryDatasets() {
      const history = this.m.history;
      const datasets = [{label: 'Processus (Mo)', data: history.map(sample => megabytes(sample.rssBytes)), color: PALETTE[0]}];
      if (history.some(sample => sample.heapUsedBytes !== null))
        datasets.push({label: 'Tas JavaScript (Mo)', data: history.map(sample => megabytes(sample.heapUsedBytes)), color: PALETTE[1]});
      if (history.some(sample => sample.containerMemoryBytes !== null))
        datasets.push({label: 'Container (Mo)', data: history.map(sample => megabytes(sample.containerMemoryBytes)), color: PALETTE[2]});
      return datasets;
    },
    cpuDatasets() {
      const history = this.m.history;
      const datasets = [{label: 'Processus (%)', data: history.map(sample => sample.cpuPercent), color: PALETTE[0]}];
      if (history.some(sample => sample.containerCpuPercent !== null))
        datasets.push({label: 'Container (%)', data: history.map(sample => sample.containerCpuPercent), color: PALETTE[2]});
      if (this.hasEventLoop)
        datasets.push({label: 'Latence (ms)', data: history.map(sample => sample.eventLoopP99Ms), color: PALETTE[7], yAxisID: 'y1', dashed: true});
      return datasets;
    },
    requestDatasets() {
      return [
        {label: 'Requêtes', data: this.r.perMinute.map(row => row.count), color: PALETTE[0]},
        {label: 'Erreurs serveur', data: this.r.perMinute.map(row => row.errors), type: 'line', color: PALETTE[7]},
      ];
    },
  },
  methods: {integer, percent, hasData, bytes, duration, dateTime, minute, usageTone},
};
</script>

<style lang="stylus" scoped>
@require '~/src/statistics/style/statistics.styl';

.alerts
  margin-bottom 14px

.muted
  margin 0
  font-size 13.5px
  color CTRL_TEXT_MUTED

.bad
  color CTRL_ERROR

.table-scroll
  overflow-x auto

.table
  width 100%
  border-collapse collapse
  font-size 13.5px

  th, td
    padding 9px 10px
    text-align left
    white-space nowrap

  th
    font-size 12.5px
    font-weight 600
    color CTRL_TEXT_MUTED
    border-bottom 1px solid #e5e5e5

  td
    color #3f3931
    border-bottom 1px solid #f1f3f8

  code
    font-size 12.5px

  .number
    text-align right

.meters
  display flex
  flex-direction column
  gap 16px

.facts
  display grid
  grid-template-columns repeat(auto-fill, minmax(170px, 1fr))
  gap 12px 20px
  margin 0

  dt
    font-size 12px
    color CTRL_TEXT_MUTED

  dd
    margin 2px 0 0
    font-size 13.5px
    font-weight 600
    color #3f3931
    overflow-wrap anywhere

  &.folders
    margin-top 18px

  &.info
    grid-template-columns repeat(auto-fill, minmax(220px, 1fr))
</style>
